import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useBilling } from '../../context/BillingContext'
import { motion, AnimatePresence } from 'framer-motion'
import {
  LayoutDashboard,
  Calendar,
  CalendarClock,
  Settings,
  LogOut,
  BarChart3,
  Building2,
  Lightbulb,
  ChevronLeft,
  ChevronRight,
  Menu,
  X,
  CheckCircle,
  Shield,
  Video,
  Bot,
  Stethoscope,
  Plug,
  ClipboardList,
} from 'lucide-react'
import { useState, useEffect, Fragment } from 'react'
import { useAuth } from '../../context/AuthContext'
import { useOrg } from '../../context/OrgContext'
import { useZoomOAuth } from '../../context/ZoomOAuthContext'
import { decodeHtmlEntities } from '../../utils/displayText'
import { isPlatformAdminEmail } from '../../utils/platformAdmin'
import { isExternalJoinEnabled } from '../../config/externalJoinBeta'

/** Sidebar active state */
function isNavLinkActive(path, pathname) {
  if (path === '/dashboard') {
    return pathname === path
  }
  return pathname === path || (path !== '/dashboard' && pathname.startsWith(`${path}/`))
}

function Sidebar() {
  const [collapsed, setCollapsed] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)
  const location = useLocation()
  const navigate = useNavigate()
  const { user, logout } = useAuth()
  const { org, isAdmin } = useOrg()

  const isPlatformAdmin = isPlatformAdminEmail(user?.email)
  const { isConnected: zoomConnected, email: zoomEmail } = useZoomOAuth()
  const billing = useBilling()

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false)
  }, [location.pathname])

  // Close mobile menu on window resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setMobileOpen(false)
      }
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Meeting-creation actions, rendered directly below the Dashboard nav item.
  // Send My Proxy / Join a Meeting stay behind the same gates as the old hero cards.
  const meetingActions = [
    { icon: <Video className="w-5 h-5" />, label: 'Host a Meeting', path: '/agenda' },
    {
      icon: <Bot className="w-5 h-5" />,
      label: 'Send My Proxy',
      path: '/leader-proxy',
      show: isPlatformAdmin,
    },
    {
      icon: <Video className="w-5 h-5" />,
      label: 'Join a Meeting',
      path: '/join-external',
      show: isExternalJoinEnabled(user?.email),
    },
  ].filter((a) => a.show !== false)

  const navItems = [
    { icon: <LayoutDashboard className="w-5 h-5" />, label: 'Dashboard', path: '/dashboard' },
    {
      icon: <ClipboardList className="w-5 h-5" />,
      label: 'Decision Console',
      path: '/console',
      platformAdminOnly: true,
    },
    { icon: <CalendarClock className="w-5 h-5" />, label: 'Meetings', path: '/meetings' },
    { icon: <Stethoscope className="w-5 h-5" />, label: 'Diagnostics', path: '/diagnostics' },
    { icon: <BarChart3 className="w-5 h-5" />, label: 'Analytics', path: '/analytics' },
    { icon: <Lightbulb className="w-5 h-5" />, label: 'Strategy', path: '/strategy' },
    {
      icon: <Building2 className="w-5 h-5" />,
      label: 'Company Analysis',
      path: '/company-analysis',
      adminOnly: true,
    },
    { icon: <Plug className="w-5 h-5" />, label: 'Connections', path: '/connections' },
    { icon: <Calendar className="w-5 h-5" />, label: 'Calendar', path: '/calendar' },
    { icon: <Settings className="w-5 h-5" />, label: 'Settings', path: '/settings' },
    {
      icon: <Shield className="w-5 h-5" />,
      label: 'Platform Admin',
      path: '/platform-admin',
      platformAdminOnly: true,
    },
  ]

  const handleLogout = () => {
    logout()
    navigate('/')
  }

  const sidebarContent = (isMobile = false) => (
    <>
      {/* Logo */}
      <div className="h-20 flex items-center justify-between px-4 border-b border-gray-200">
        <Link to="/" className="flex items-center gap-3">
          <div className="w-12 h-10 flex items-center justify-center flex-shrink-0">
            <img src="/rithmo-mark.png" alt="Rithmo logo" className="w-full h-full object-contain" />
          </div>
          {(isMobile || !collapsed) && (
            <motion.span
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-xl font-bold text-gray-900"
            >
              Rithmo
            </motion.span>
          )}
        </Link>
        {!isMobile && (
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="p-2 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-all"
          >
            {collapsed ? <ChevronRight className="w-5 h-5" /> : <ChevronLeft className="w-5 h-5" />}
          </button>
        )}
        {isMobile && (
          <button
            onClick={() => setMobileOpen(false)}
            className="p-2 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-all"
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Zoom connection status — only show when disconnected */}
      {!zoomConnected && (
        <div
          className={`mx-4 mb-3 flex items-center gap-2 rounded-lg bg-amber-50 px-3 py-2 text-amber-700 ${
            !isMobile && collapsed ? 'justify-center px-2' : ''
          }`}
          title="Zoom not connected"
        >
          <CheckCircle className="w-4 h-4 flex-shrink-0 text-amber-600" />
          {(isMobile || !collapsed) && (
            <span className="truncate text-xs font-medium">
              Zoom not connected
            </span>
          )}
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto px-3 py-2 space-y-1">
        {navItems.filter((item) => {
          if (item.adminOnly && !isAdmin) return false
          if (item.platformAdminOnly && !isPlatformAdmin) return false
          return true
        }).map((item, index) => {
          const pathname = location.pathname
          const isActive = isNavLinkActive(item.path, pathname)

          return (
            <Fragment key={item.path}>
              <Link
                to={item.path}
                className={`nav-link ${isActive ? 'active' : ''} ${
                  !isMobile && collapsed ? 'justify-center px-3' : ''
                }`}
              >
                {item.icon}
                {(isMobile || !collapsed) && <span>{item.label}</span>}
              </Link>

              {/* Meeting-creation actions sit directly below the Dashboard item */}
              {index === 0 && meetingActions.length > 0 && (
                <div className="my-2 rounded-xl border border-gray-200 bg-gray-50/70 p-1 space-y-0.5">
                  {meetingActions.map((action) => {
                    const actionActive = isNavLinkActive(action.path, location.pathname)
                    return (
                      <Link
                        key={action.path}
                        to={action.path}
                        title={action.label}
                        className={`nav-link text-primary-700 hover:bg-primary-50 ${
                          actionActive ? 'active' : ''
                        } ${!isMobile && collapsed ? 'justify-center px-3' : ''}`}
                      >
                        {action.icon}
                        {(isMobile || !collapsed) && <span>{action.label}</span>}
                      </Link>
                    )
                  })}
                </div>
              )}
            </Fragment>
          )
        })}
      </nav>

      {/* User Section */}
      <div className="p-4 border-t border-gray-200">
        <div className={`flex items-center gap-3 ${!isMobile && collapsed ? 'justify-center' : ''}`}>
          <div className="avatar flex-shrink-0 overflow-hidden">
            {user?.avatar ? (
              <img
                src={user.avatar}
                alt={`${user?.name || 'User'} avatar`}
                className="w-full h-full object-cover"
              />
            ) : (
              user?.name?.[0]?.toUpperCase() || 'U'
            )}
          </div>
          {(isMobile || !collapsed) && (
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">
                {user?.name || 'User'}
              </p>
              <p className="text-xs text-gray-500 truncate">
                {user?.email || 'user@example.com'}
              </p>
            </div>
          )}
        </div>

        {org && (isMobile || !collapsed) && (
          <div className="mt-2 pt-2 border-t border-gray-100">
            <p className="text-xs font-medium text-gray-700 truncate">{decodeHtmlEntities(org.name)}</p>
            <p className="text-xs text-gray-500">{isAdmin ? 'Org Admin' : 'Org Member'}</p>
          </div>
        )}

        {billing.enforced && isAdmin && billing.usage && (isMobile || !collapsed) && (
          <Link
            to="/settings?tab=plans"
            className="mt-2 block rounded-lg border border-gray-100 bg-gray-50 px-3 py-2 hover:bg-gray-100 transition-colors"
          >
            <p className="text-xs font-semibold text-gray-700">
              Remaining: {Math.round(billing.usage.remainingMinutes / 60)}h / {Math.round(billing.usage.includedMinutes / 60)}h
            </p>
            <div className="mt-1 h-1.5 w-full rounded-full bg-gray-200 overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${
                  billing.usage.remainingMinutes / billing.usage.includedMinutes < 0.15
                    ? 'bg-red-500'
                    : billing.usage.remainingMinutes / billing.usage.includedMinutes < 0.3
                      ? 'bg-amber-500'
                      : 'bg-primary-500'
                }`}
                style={{ width: `${Math.min(100, (billing.usage.usedMinutes / Math.max(1, billing.usage.includedMinutes)) * 100)}%` }}
              />
            </div>
            <p className="text-[10px] text-gray-500 mt-1 capitalize">{billing.plan?.name || 'Free'} Plan</p>
          </Link>
        )}

        <button
          onClick={handleLogout}
          className={`nav-link text-red-500 hover:text-red-600 hover:bg-red-50 w-full ${
            !isMobile && collapsed ? 'justify-center px-3' : ''
          }`}
        >
          <LogOut className="w-5 h-5" />
          {(isMobile || !collapsed) && <span>Sign Out</span>}
        </button>
      </div>
    </>
  )

  return (
    <>
      {/* Mobile Menu Button */}
      <button
        onClick={() => setMobileOpen(true)}
        className="lg:hidden fixed top-4 left-4 z-50 p-3 rounded-xl bg-white/80 backdrop-blur-sm border border-gray-200 text-gray-700 shadow-sm"
        aria-label="Open menu"
      >
        <Menu className="w-6 h-6" />
      </button>

      {/* Mobile Overlay */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setMobileOpen(false)}
            className="lg:hidden fixed inset-0 z-40 bg-black/30 backdrop-blur-sm"
          />
        )}
      </AnimatePresence>

      {/* Mobile Sidebar */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.aside
            initial={{ x: -300 }}
            animate={{ x: 0 }}
            exit={{ x: -300 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="lg:hidden fixed left-0 top-0 bottom-0 w-[280px] z-50 bg-white/95 backdrop-blur-xl border-r border-gray-200 flex flex-col"
          >
            {sidebarContent(true)}
          </motion.aside>
        )}
      </AnimatePresence>

      {/* Desktop Sidebar */}
      <motion.aside
        initial={false}
        animate={{ width: collapsed ? 80 : 280 }}
        className="hidden lg:flex fixed left-0 top-0 bottom-0 z-40 bg-white/80 backdrop-blur-xl border-r border-gray-200 flex-col shadow-sm"
      >
        {sidebarContent(false)}
      </motion.aside>
    </>
  )
}

export default Sidebar
